/**
 * 360度 (全天球 / 正距円筒図法) 画像のパノラマビューア (docs/27 §4)。
 *
 * ストリートビューのようにドラッグ / スワイプで見回し、ホイール・ピンチで拡縮する。外部ライブラリに
 * 依存しない WebGL の全画面クワッド + フラグメントシェーダ実装 (three.js 等を入れると user PWA の
 * バンドルが大きく膨らむため)。座標規約は `@citizen-walk/shared-types` の `equirectMath` と同一で、
 * `renderOverlay` に現在の視点 (heading / pitch / hfov) とコンテナサイズを渡すので、呼出側は
 * `projectAzElToPerspective` でピン等を重畳できる (管理画面の机上調査 3D 重畳が利用)。
 *
 * 使い方:
 *   <PanoramaViewer src={url} baseHeadingDeg={post.photo.poseHeadingDeg ?? 0} className="h-80" />
 *
 * - `interactive=false` + `pov` 指定で「固定視点の静止画」として描画できる (机上調査の 2D 画像ボードが
 *   サーバ側切り出しと同じ 4 方向をブラウザで再現するのに使う)。
 * - WebGL が使えない端末では通常の <img> にフォールバックする。
 */
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';

export interface PanoramaPov {
  /** 視線の方位 (deg、北0・時計回り)。 */
  heading: number;
  /** 視線の仰角 (deg、上が正)。 */
  pitch: number;
  /** 水平画角 (deg)。小さいほどズームイン。 */
  hfov: number;
}

export interface PanoramaViewportState extends PanoramaPov {
  width: number;
  height: number;
}

export interface PanoramaViewerProps {
  /** 全天球画像の URL (CORS 許可が必要: Firebase Storage の download URL は bucket CORS で許可済)。 */
  src: string | null;
  /** 画像中央列の方位 (XMP GPano PoseHeadingDegrees)。未知なら 0。 */
  baseHeadingDeg?: number;
  /** 初期視点 (非制御)。既定は baseHeading の方角・水平・hfov 90。 */
  initialPov?: Partial<PanoramaPov>;
  /** 制御モード。指定時は内部状態を持たず、この視点で描画する。 */
  pov?: PanoramaPov;
  onPovChange?: (pov: PanoramaPov) => void;
  /** ドラッグ・ホイール等の操作を受け付けるか (既定 true)。 */
  interactive?: boolean;
  minHfov?: number;
  maxHfov?: number;
  className?: string;
  style?: CSSProperties;
  /** 視点に追従するオーバーレイ (ピン等)。pointer-events は既定 none、要素側で auto にする。 */
  renderOverlay?: (state: PanoramaViewportState) => ReactNode;
  /** ズーム / リセット / 全画面 / ジャイロ のボタン群を表示するか (既定 = interactive)。 */
  showControls?: boolean;
  /** 端末の向きに追従するボタンを出すか (既定 false)。 */
  enableGyro?: boolean;
  /** 全画面ボタンを出すか (既定 true)。 */
  enableFullscreen?: boolean;
  /** 方位コンパスを出すか (既定 true)。 */
  showCompass?: boolean;
  onLoad?: (info: { width: number; height: number }) => void;
  onError?: (message: string) => void;
  alt?: string;
  'data-testid'?: string;
}

const DEFAULT_HFOV = 90;
const MIN_HFOV = 30;
const MAX_HFOV = 120;
const PITCH_LIMIT = 89;
const INERTIA_DECAY = 0.92;
const INERTIA_STOP = 0.02;
const DPR_CAP = 2;

const VERT = `
attribute vec2 aPos;
varying vec2 vNdc;
void main() {
  vNdc = aPos;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

// フラグメントシェーダ: 画面上の各画素 → 視線方向 → 方位/仰角 → 正距円筒 uv (equirectMath と同じ規約)。
// WebGL2 では mipmap + textureGrad で、u の巻き戻し (fract) による継ぎ目の LOD 破綻を回避する。
const FRAG_BODY = `
uniform sampler2D uTex;
uniform float uTanHalfH;
uniform float uTanHalfV;
uniform float uHeading;
uniform float uPitch;
uniform float uBaseHeading;
const float PI = 3.141592653589793;
vec2 computeUv(vec2 ndc, out float x) {
  vec3 d = normalize(vec3(ndc.x * uTanHalfH, ndc.y * uTanHalfV, 1.0));
  float cp = cos(uPitch), sp = sin(uPitch);
  d = vec3(d.x, d.y * cp + d.z * sp, -d.y * sp + d.z * cp);
  float ch = cos(uHeading), sh = sin(uHeading);
  d = vec3(d.x * ch + d.z * sh, d.y, -d.x * sh + d.z * ch);
  float az = atan(d.x, d.z);
  float el = asin(clamp(d.y, -1.0, 1.0));
  x = (az - uBaseHeading) / (2.0 * PI) + 0.5;
  return vec2(fract(x), 0.5 - el / PI);
}`;

const FRAG_WEBGL2 = `#version 300 es
precision highp float;
in vec2 vNdc;
out vec4 outColor;
${FRAG_BODY}
void main() {
  float x;
  vec2 uv = computeUv(vNdc, x);
  // 継ぎ目 (u=0/1) で fract の微分が跳ねて LOD が最粗になるのを、半周ずらした座標の微分で補う。
  float x2 = fract(x + 0.5);
  vec2 g1 = vec2(dFdx(uv.x), dFdy(uv.x));
  vec2 g2 = vec2(dFdx(x2), dFdy(x2));
  vec2 gu = dot(g1, g1) < dot(g2, g2) ? g1 : g2;
  outColor = textureGrad(uTex, uv, vec2(gu.x, dFdx(uv.y)), vec2(gu.y, dFdy(uv.y)));
}`;

const FRAG_WEBGL1 = `
precision highp float;
varying vec2 vNdc;
${FRAG_BODY}
void main() {
  float x;
  vec2 uv = computeUv(vNdc, x);
  gl_FragColor = texture2D(uTex, uv);
}`;

const VERT_WEBGL2 = `#version 300 es
in vec2 aPos;
out vec2 vNdc;
void main() {
  vNdc = aPos;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

interface GlState {
  gl: WebGLRenderingContext | WebGL2RenderingContext;
  isWebGl2: boolean;
  program: WebGLProgram;
  texture: WebGLTexture | null;
  uniforms: {
    tanHalfH: WebGLUniformLocation | null;
    tanHalfV: WebGLUniformLocation | null;
    heading: WebGLUniformLocation | null;
    pitch: WebGLUniformLocation | null;
    baseHeading: WebGLUniformLocation | null;
  };
}

function compile(gl: WebGLRenderingContext, type: number, source: string): WebGLShader {
  const shader = gl.createShader(type);
  if (!shader) throw new Error('shader unavailable');
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(shader) ?? '';
    gl.deleteShader(shader);
    throw new Error(`shader compile failed: ${log}`);
  }
  return shader;
}

function initGl(canvas: HTMLCanvasElement): GlState | null {
  const attrs: WebGLContextAttributes = {
    antialias: false,
    alpha: false,
    preserveDrawingBuffer: false,
    powerPreference: 'high-performance',
  };
  let gl: WebGLRenderingContext | WebGL2RenderingContext | null = null;
  let isWebGl2 = false;
  try {
    gl = canvas.getContext('webgl2', attrs) as WebGL2RenderingContext | null;
    isWebGl2 = !!gl;
    if (!gl) gl = canvas.getContext('webgl', attrs) as WebGLRenderingContext | null;
  } catch {
    gl = null;
  }
  if (!gl) return null;
  const program = gl.createProgram();
  if (!program) return null;
  try {
    gl.attachShader(program, compile(gl, gl.VERTEX_SHADER, isWebGl2 ? VERT_WEBGL2 : VERT));
    gl.attachShader(program, compile(gl, gl.FRAGMENT_SHADER, isWebGl2 ? FRAG_WEBGL2 : FRAG_WEBGL1));
  } catch {
    return null;
  }
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return null;
  gl.useProgram(program);
  const buffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
  const aPos = gl.getAttribLocation(program, 'aPos');
  gl.enableVertexAttribArray(aPos);
  gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);
  return {
    gl,
    isWebGl2,
    program,
    texture: null,
    uniforms: {
      tanHalfH: gl.getUniformLocation(program, 'uTanHalfH'),
      tanHalfV: gl.getUniformLocation(program, 'uTanHalfV'),
      heading: gl.getUniformLocation(program, 'uHeading'),
      pitch: gl.getUniformLocation(program, 'uPitch'),
      baseHeading: gl.getUniformLocation(program, 'uBaseHeading'),
    },
  };
}

function isPowerOfTwo(n: number): boolean {
  return n > 0 && (n & (n - 1)) === 0;
}

/** テクスチャに載せる画像を用意する (最大サイズ超過は縮小、WebGL2 では 2 のべき乗に揃えて mipmap 可能に)。 */
function prepareTextureSource(
  img: HTMLImageElement,
  maxSize: number,
  preferPot: boolean,
): TexImageSource {
  let width = img.naturalWidth;
  let height = img.naturalHeight;
  const needsResize = width > maxSize || height > maxSize;
  const needsPot = preferPot && !(isPowerOfTwo(width) && isPowerOfTwo(height));
  if (!needsResize && !needsPot) return img;
  if (needsResize) {
    const scale = Math.min(maxSize / width, maxSize / height);
    width = Math.floor(width * scale);
    height = Math.floor(height * scale);
  }
  if (preferPot) {
    // 2:1 を保ったまま、幅を 2 のべき乗に丸める (切り上げすぎない: 直近の下のべき乗)。
    const potW = Math.min(maxSize, 2 ** Math.floor(Math.log2(width)));
    width = potW;
    height = Math.max(1, potW / 2);
  }
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return img;
  ctx.drawImage(img, 0, 0, width, height);
  return canvas;
}

function clampPitch(p: number): number {
  return Math.max(-PITCH_LIMIT, Math.min(PITCH_LIMIT, p));
}

function normHeading(h: number): number {
  return ((h % 360) + 360) % 360;
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

type GyroPermissionRequester = { requestPermission?: () => Promise<'granted' | 'denied'> };

export function PanoramaViewer({
  src,
  baseHeadingDeg = 0,
  initialPov,
  pov: controlledPov,
  onPovChange,
  interactive = true,
  minHfov = MIN_HFOV,
  maxHfov = MAX_HFOV,
  className,
  style,
  renderOverlay,
  showControls,
  enableGyro = false,
  enableFullscreen = true,
  showCompass = true,
  onLoad,
  onError,
  alt = '360度写真',
  'data-testid': testId,
}: PanoramaViewerProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const glRef = useRef<GlState | null>(null);
  const rafRef = useRef<number | null>(null);
  const inertiaRef = useRef<number | null>(null);
  const [status, setStatus] = useState<'idle' | 'loading' | 'ready' | 'fallback' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [size, setSize] = useState<{ width: number; height: number }>({ width: 0, height: 0 });
  const [fullscreen, setFullscreen] = useState(false);
  const [gyroActive, setGyroActive] = useState(false);

  const isControlled = controlledPov !== undefined;
  const [internalPov, setInternalPov] = useState<PanoramaPov>(() => ({
    heading: normHeading(initialPov?.heading ?? baseHeadingDeg),
    pitch: clampPitch(initialPov?.pitch ?? 0),
    hfov: Math.min(maxHfov, Math.max(minHfov, initialPov?.hfov ?? DEFAULT_HFOV)),
  }));
  const pov = isControlled ? controlledPov : internalPov;
  const povRef = useRef<PanoramaPov>(pov);
  const onPovChangeRef = useRef(onPovChange);
  const baseHeadingRef = useRef(baseHeadingDeg);
  // 最新値を ref に同期 (描画中に ref を触らない: react-hooks/refs)。
  useEffect(() => {
    povRef.current = pov;
  }, [pov]);
  useEffect(() => {
    onPovChangeRef.current = onPovChange;
  }, [onPovChange]);
  useEffect(() => {
    baseHeadingRef.current = baseHeadingDeg;
  }, [baseHeadingDeg]);
  const [dragging, setDragging] = useState(false);

  const setPov = useCallback(
    (next: PanoramaPov) => {
      const normalized: PanoramaPov = {
        heading: normHeading(next.heading),
        pitch: clampPitch(next.pitch),
        hfov: Math.min(maxHfov, Math.max(minHfov, next.hfov)),
      };
      povRef.current = normalized;
      if (!isControlled) setInternalPov(normalized);
      onPovChangeRef.current?.(normalized);
    },
    [isControlled, maxHfov, minHfov],
  );

  // ---- 描画 --------------------------------------------------------------------------
  const draw = useCallback(() => {
    const state = glRef.current;
    const canvas = canvasRef.current;
    if (!state || !canvas || !state.texture) return;
    const { gl, uniforms } = state;
    const { heading, pitch, hfov } = povRef.current;
    const w = canvas.width;
    const h = canvas.height;
    if (w === 0 || h === 0) return;
    gl.viewport(0, 0, w, h);
    const tanHalfH = Math.tan((hfov / 2) * (Math.PI / 180));
    const tanHalfV = tanHalfH * (h / w);
    gl.useProgram(state.program);
    gl.uniform1f(uniforms.tanHalfH, tanHalfH);
    gl.uniform1f(uniforms.tanHalfV, tanHalfV);
    gl.uniform1f(uniforms.heading, heading * (Math.PI / 180));
    gl.uniform1f(uniforms.pitch, pitch * (Math.PI / 180));
    gl.uniform1f(uniforms.baseHeading, baseHeadingRef.current * (Math.PI / 180));
    gl.bindTexture(gl.TEXTURE_2D, state.texture);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  }, []);

  const requestDraw = useCallback(() => {
    if (rafRef.current !== null) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      draw();
    });
  }, [draw]);

  // 視点・baseHeading が変わったら再描画。
  useEffect(() => {
    requestDraw();
  }, [pov.heading, pov.pitch, pov.hfov, baseHeadingDeg, requestDraw]);

  // ---- コンテナサイズ追従 ------------------------------------------------------------
  useLayoutEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const update = () => {
      const width = el.clientWidth;
      const height = el.clientHeight;
      setSize((s) => (s.width === width && s.height === height ? s : { width, height }));
      const canvas = canvasRef.current;
      if (canvas) {
        const dpr = Math.min(DPR_CAP, window.devicePixelRatio || 1);
        const cw = Math.max(1, Math.round(width * dpr));
        const ch = Math.max(1, Math.round(height * dpr));
        if (canvas.width !== cw || canvas.height !== ch) {
          canvas.width = cw;
          canvas.height = ch;
        }
      }
      requestDraw();
    };
    update();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [requestDraw, fullscreen]);

  // ---- 画像読込 + テクスチャ --------------------------------------------------------
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!src || !canvas) {
      setStatus('idle');
      return;
    }
    let cancelled = false;
    setStatus('loading');
    setErrorMessage(null);

    if (!glRef.current) {
      glRef.current = initGl(canvas);
      if (!glRef.current) {
        setStatus('fallback');
        return;
      }
    }
    const state = glRef.current;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.decoding = 'async';
    img.onload = () => {
      if (cancelled) return;
      try {
        const { gl, isWebGl2 } = state;
        const maxSize = Math.min(8192, gl.getParameter(gl.MAX_TEXTURE_SIZE) as number);
        const source = prepareTextureSource(img, maxSize, isWebGl2);
        const texture = state.texture ?? gl.createTexture();
        if (!texture) throw new Error('texture unavailable');
        state.texture = texture;
        gl.bindTexture(gl.TEXTURE_2D, texture);
        gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, gl.RGB, gl.UNSIGNED_BYTE, source);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        const w = (source as { width: number }).width;
        const h = (source as { height: number }).height;
        if (isWebGl2 && isPowerOfTwo(w) && isPowerOfTwo(h)) {
          gl.generateMipmap(gl.TEXTURE_2D);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
          const aniso =
            gl.getExtension('EXT_texture_filter_anisotropic') ??
            gl.getExtension('WEBKIT_EXT_texture_filter_anisotropic');
          if (aniso) {
            const max = gl.getParameter(aniso.MAX_TEXTURE_MAX_ANISOTROPY_EXT) as number;
            gl.texParameterf(gl.TEXTURE_2D, aniso.TEXTURE_MAX_ANISOTROPY_EXT, Math.min(8, max));
          }
        } else {
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        }
        setStatus('ready');
        onLoad?.({ width: img.naturalWidth, height: img.naturalHeight });
        requestDraw();
      } catch (e) {
        setStatus('fallback');
        onError?.((e as Error).message);
      }
    };
    img.onerror = () => {
      if (cancelled) return;
      setStatus('error');
      const message = '360度写真を読み込めませんでした。';
      setErrorMessage(message);
      onError?.(message);
    };
    img.src = src;
    return () => {
      cancelled = true;
    };
    // onLoad/onError は ref 的に扱う (毎レンダー変わる関数で再読込しない)。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, requestDraw]);

  // アンマウント時に GL 資源を解放。
  useEffect(() => {
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      if (inertiaRef.current !== null) cancelAnimationFrame(inertiaRef.current);
      const state = glRef.current;
      if (state) {
        if (state.texture) state.gl.deleteTexture(state.texture);
        state.gl.deleteProgram(state.program);
        const lose = state.gl.getExtension('WEBGL_lose_context');
        lose?.loseContext();
      }
      glRef.current = null;
    };
  }, []);

  // ---- 操作 (ドラッグ / 慣性 / ピンチ / ホイール / キーボード) ----------------------------
  const pointersRef = useRef<Map<number, { x: number; y: number }>>(new Map());
  const dragRef = useRef<{
    lastX: number;
    lastY: number;
    lastT: number;
    vx: number;
    vy: number;
    pinchDist: number | null;
  } | null>(null);

  const stopInertia = useCallback(() => {
    if (inertiaRef.current !== null) {
      cancelAnimationFrame(inertiaRef.current);
      inertiaRef.current = null;
    }
  }, []);

  const degPerPx = useCallback(() => {
    const w = size.width || 1;
    return povRef.current.hfov / w;
  }, [size.width]);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!interactive) return;
    stopInertia();
    if (gyroActive) setGyroActive(false);
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    setDragging(true);
    dragRef.current = {
      lastX: e.clientX,
      lastY: e.clientY,
      lastT: performance.now(),
      vx: 0,
      vy: 0,
      pinchDist: null,
    };
    if (pointersRef.current.size === 2) {
      const [a, b] = [...pointersRef.current.values()];
      if (a && b) dragRef.current.pinchDist = Math.hypot(a.x - b.x, a.y - b.y);
    }
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!interactive || !dragRef.current) return;
    const pointers = pointersRef.current;
    if (!pointers.has(e.pointerId)) return;
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const drag = dragRef.current;
    if (pointers.size >= 2) {
      const [a, b] = [...pointers.values()];
      if (!a || !b) return;
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      if (drag.pinchDist && dist > 0) {
        const ratio = drag.pinchDist / dist;
        setPov({ ...povRef.current, hfov: povRef.current.hfov * ratio });
      }
      drag.pinchDist = dist;
      drag.lastX = e.clientX;
      drag.lastY = e.clientY;
      return;
    }
    const now = performance.now();
    const dx = e.clientX - drag.lastX;
    const dy = e.clientY - drag.lastY;
    const dt = Math.max(1, now - drag.lastT);
    const k = degPerPx();
    const next = {
      ...povRef.current,
      heading: povRef.current.heading - dx * k,
      pitch: povRef.current.pitch + dy * k,
    };
    setPov(next);
    // 直近の速度 (deg/ms)。慣性用。
    drag.vx = (-dx * k) / dt;
    drag.vy = (dy * k) / dt;
    drag.lastX = e.clientX;
    drag.lastY = e.clientY;
    drag.lastT = now;
  };

  const endPointer = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!interactive) return;
    const pointers = pointersRef.current;
    pointers.delete(e.pointerId);
    (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
    if (pointers.size > 0) {
      if (dragRef.current) dragRef.current.pinchDist = null;
      const rest = [...pointers.values()][0];
      if (rest && dragRef.current) {
        dragRef.current.lastX = rest.x;
        dragRef.current.lastY = rest.y;
        dragRef.current.vx = 0;
        dragRef.current.vy = 0;
      }
      return;
    }
    const drag = dragRef.current;
    dragRef.current = null;
    setDragging(false);
    if (!drag || prefersReducedMotion()) return;
    // 指を離してから時間が経っていれば慣性なし。
    if (performance.now() - drag.lastT > 80) return;
    let vx = drag.vx * 16; // deg/frame (≈16ms)
    let vy = drag.vy * 16;
    if (Math.abs(vx) < INERTIA_STOP && Math.abs(vy) < INERTIA_STOP) return;
    const step = () => {
      vx *= INERTIA_DECAY;
      vy *= INERTIA_DECAY;
      if (Math.abs(vx) < INERTIA_STOP && Math.abs(vy) < INERTIA_STOP) {
        inertiaRef.current = null;
        return;
      }
      setPov({
        ...povRef.current,
        heading: povRef.current.heading + vx,
        pitch: povRef.current.pitch + vy,
      });
      inertiaRef.current = requestAnimationFrame(step);
    };
    inertiaRef.current = requestAnimationFrame(step);
  };

  // ホイールは passive:false で preventDefault したいので手動登録。
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el || !interactive) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      stopInertia();
      const factor = Math.exp(e.deltaY * 0.0015);
      setPov({ ...povRef.current, hfov: povRef.current.hfov * factor });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [interactive, setPov, stopInertia]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!interactive) return;
    const p = povRef.current;
    const stepDeg = 5;
    switch (e.key) {
      case 'ArrowLeft':
        setPov({ ...p, heading: p.heading - stepDeg });
        break;
      case 'ArrowRight':
        setPov({ ...p, heading: p.heading + stepDeg });
        break;
      case 'ArrowUp':
        setPov({ ...p, pitch: p.pitch + stepDeg });
        break;
      case 'ArrowDown':
        setPov({ ...p, pitch: p.pitch - stepDeg });
        break;
      case '+':
      case '=':
        setPov({ ...p, hfov: p.hfov / 1.2 });
        break;
      case '-':
        setPov({ ...p, hfov: p.hfov * 1.2 });
        break;
      case 'Escape':
        if (fullscreen) setFullscreen(false);
        else return;
        break;
      default:
        return;
    }
    e.preventDefault();
  };

  // ---- ジャイロ追従 (best-effort) ----------------------------------------------------
  useEffect(() => {
    if (!gyroActive) return;
    const handler = (e: DeviceOrientationEvent) => {
      const compass = (e as DeviceOrientationEvent & { webkitCompassHeading?: number })
        .webkitCompassHeading;
      const heading =
        typeof compass === 'number' && Number.isFinite(compass)
          ? compass
          : e.alpha !== null && e.alpha !== undefined
            ? 360 - e.alpha
            : null;
      if (heading === null) return;
      const beta = e.beta ?? 90;
      setPov({ ...povRef.current, heading, pitch: beta - 90 });
    };
    const absEvent = 'ondeviceorientationabsolute' in window ? 'deviceorientationabsolute' : null;
    if (absEvent) window.addEventListener(absEvent, handler as EventListener);
    else window.addEventListener('deviceorientation', handler);
    return () => {
      if (absEvent) window.removeEventListener(absEvent, handler as EventListener);
      else window.removeEventListener('deviceorientation', handler);
    };
  }, [gyroActive, setPov]);

  const toggleGyro = async () => {
    if (gyroActive) {
      setGyroActive(false);
      return;
    }
    const requester = (
      typeof DeviceOrientationEvent !== 'undefined' ? DeviceOrientationEvent : {}
    ) as GyroPermissionRequester;
    try {
      if (typeof requester.requestPermission === 'function') {
        const result = await requester.requestPermission();
        if (result !== 'granted') return;
      }
      setGyroActive(true);
    } catch {
      /* 拒否・非対応は無視 */
    }
  };

  // ---- 全画面 (CSS 方式、iOS Safari でも動く) --------------------------------------------
  useEffect(() => {
    if (!fullscreen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFullscreen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [fullscreen]);

  const resetPov = () =>
    setPov({
      heading: initialPov?.heading ?? baseHeadingDeg,
      pitch: initialPov?.pitch ?? 0,
      hfov: initialPov?.hfov ?? DEFAULT_HFOV,
    });

  const overlay = useMemo(
    () =>
      renderOverlay && size.width > 0 && size.height > 0
        ? renderOverlay({ ...pov, width: size.width, height: size.height })
        : null,
    [renderOverlay, pov, size.width, size.height],
  );

  const controlsVisible = showControls ?? interactive;
  const wrapperClass = [
    'relative select-none overflow-hidden bg-slate-900',
    fullscreen ? 'fixed inset-0 z-[1000] rounded-none' : '',
    !fullscreen && className ? className : '',
    interactive ? (dragging ? 'cursor-grabbing' : 'cursor-grab') : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      ref={wrapperRef}
      className={wrapperClass}
      style={fullscreen ? undefined : style}
      data-testid={testId ?? 'panorama-viewer'}
      role={interactive ? 'application' : 'img'}
      aria-label={alt}
      tabIndex={interactive ? 0 : undefined}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endPointer}
      onPointerCancel={endPointer}
      onKeyDown={onKeyDown}
      onDoubleClick={
        interactive
          ? () => setPov({ ...povRef.current, hfov: povRef.current.hfov > 60 ? 45 : DEFAULT_HFOV })
          : undefined
      }
    >
      {status === 'fallback' && src ? (
        <img src={src} alt={alt} className="w-full h-full object-contain" draggable={false} />
      ) : (
        <canvas
          ref={canvasRef}
          className="block w-full h-full"
          style={{ touchAction: interactive ? 'none' : 'auto' }}
          aria-hidden="true"
        />
      )}

      {overlay && (
        <div
          className="absolute inset-0 overflow-hidden"
          style={{ pointerEvents: 'none', zIndex: 10 }}
        >
          {overlay}
        </div>
      )}

      {status === 'loading' && (
        <div className="absolute inset-0 flex items-center justify-center text-xs text-white/80 bg-slate-900/40">
          360度写真を読込中…
        </div>
      )}
      {status === 'error' && (
        <div className="absolute inset-0 flex items-center justify-center text-xs text-white/90 bg-slate-900/70 px-4 text-center">
          {errorMessage}
        </div>
      )}
      {status === 'fallback' && (
        <div className="absolute left-2 bottom-2 rounded bg-black/60 text-white text-[11px] px-2 py-1">
          この端末では360度表示に対応していないため、平面画像で表示しています
        </div>
      )}

      {showCompass && status === 'ready' && (
        <div
          className="absolute left-2 top-2 flex items-center gap-1 rounded-full bg-black/50 text-white text-[11px] px-2 py-1"
          style={{ zIndex: 20, pointerEvents: 'none' }}
          aria-hidden="true"
        >
          <span
            className="inline-block w-3 h-3 rounded-full border border-white/70 relative"
            style={{ transform: `rotate(${-pov.heading}deg)` }}
          >
            <span className="absolute left-1/2 top-0 -translate-x-1/2 w-0 h-0 border-l-[3px] border-r-[3px] border-b-[5px] border-l-transparent border-r-transparent border-b-red-400" />
          </span>
          {Math.round(pov.heading)}°
        </div>
      )}

      {controlsVisible && status === 'ready' && (
        <div className="absolute right-2 bottom-2 flex flex-col gap-1" style={{ zIndex: 20 }}>
          <button
            type="button"
            className="w-9 h-9 rounded-full bg-black/55 text-white text-lg leading-none hover:bg-black/75 focus:outline-none focus:ring-2 focus:ring-white/70"
            aria-label="ズームイン"
            onClick={(e) => {
              e.stopPropagation();
              setPov({ ...povRef.current, hfov: povRef.current.hfov / 1.25 });
            }}
            onPointerDown={(e) => e.stopPropagation()}
          >
            +
          </button>
          <button
            type="button"
            className="w-9 h-9 rounded-full bg-black/55 text-white text-lg leading-none hover:bg-black/75 focus:outline-none focus:ring-2 focus:ring-white/70"
            aria-label="ズームアウト"
            onClick={(e) => {
              e.stopPropagation();
              setPov({ ...povRef.current, hfov: povRef.current.hfov * 1.25 });
            }}
            onPointerDown={(e) => e.stopPropagation()}
          >
            −
          </button>
          <button
            type="button"
            className="w-9 h-9 rounded-full bg-black/55 text-white text-sm hover:bg-black/75 focus:outline-none focus:ring-2 focus:ring-white/70"
            aria-label="視点をリセット"
            title="視点をリセット"
            onClick={(e) => {
              e.stopPropagation();
              resetPov();
            }}
            onPointerDown={(e) => e.stopPropagation()}
          >
            ⟲
          </button>
          {enableGyro && (
            <button
              type="button"
              className={`w-9 h-9 rounded-full text-white text-sm hover:bg-black/75 focus:outline-none focus:ring-2 focus:ring-white/70 ${gyroActive ? 'bg-primary-600' : 'bg-black/55'}`}
              aria-label={gyroActive ? '端末の向きへの追従をやめる' : '端末の向きに追従する'}
              aria-pressed={gyroActive}
              title="端末の向きに追従"
              onClick={(e) => {
                e.stopPropagation();
                void toggleGyro();
              }}
              onPointerDown={(e) => e.stopPropagation()}
            >
              🧭
            </button>
          )}
          {enableFullscreen && (
            <button
              type="button"
              className="w-9 h-9 rounded-full bg-black/55 text-white text-sm hover:bg-black/75 focus:outline-none focus:ring-2 focus:ring-white/70"
              aria-label={fullscreen ? '全画面を終了' : '全画面で表示'}
              title={fullscreen ? '全画面を終了' : '全画面で表示'}
              onClick={(e) => {
                e.stopPropagation();
                setFullscreen((f) => !f);
              }}
              onPointerDown={(e) => e.stopPropagation()}
            >
              {fullscreen ? '✕' : '⛶'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
